import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { get, merge } from 'lodash';
import { SaveTemplateComponent, TemplateService } from '@nrwl/test-setup';
import { Subject, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';

import {
  TestSetupComponent,
  ITestSetupEntry,
  BaseSetupTab,
  TestSetupTabsComponent
} from '@nrwl/test-setup';
import {
  ValuesExchangeProvider,
  NotificationService,
  TestListService,
  TestTypeValue,
  DialogService,
  TEST_STATE
} from '@nrwl/common-lib';
import { VoipTestService } from './voip-test.service';
import { VoipTestComponent } from './component/voip-test/voip-test.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TemplateTabsComponent } from '@nrwl/template-management';
import { VoipConfigMapping as ConfigFieldMapping } from './json/voip-agent-model';
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'voip-test',
  providers: [VoipTestService],
  template: '<nrwl-test-setup-tabs [parentObj]="currentObj"></nrwl-test-setup-tabs>'
})
export class VoipTestSetupComponent
  extends TestSetupComponent
  implements OnInit, AfterViewInit, OnDestroy, ITestSetupEntry
{
  @ViewChild(TestSetupTabsComponent, { static: true }) testSetupTabRef: TestSetupTabsComponent;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  setupConfig: any;
  setupId: string;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClose = new EventEmitter<any>();
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  dataId: any;
  router: Router;
  setupService: VoipTestService;
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  openWizard: any;
  editMode: boolean;
  subscription: Subscription;
  changeDetectorRef: ChangeDetectorRef;
  //eslint-disable-next-line
  agentBasedConfig: any;
  activatedRouteSubscription: Subscription;
  currentObj: any; //eslint-disable-line
  editDeviceDialogRef: MatDialogRef<SaveTemplateComponent>;
  templateDialogRef: MatDialogRef<TemplateTabsComponent>;
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    router: Router,
    setupService: VoipTestService,
    changeDetectorRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private readonly dialogService: DialogService,
    private readonly exchange: ValuesExchangeProvider,
    private readonly activatedRoute: ActivatedRoute,
    private readonly notificationService: NotificationService,
    private readonly testListService: TestListService,
    private readonly templateService: TemplateService,
    private readonly factoryResolver: ComponentFactoryResolver
  ) {
    super();
    this.router = router;
    this.setupService = setupService;
    this.changeDetectorRef = changeDetectorRef;
  }

  ngOnInit() {
    this.currentObj = this;
    this.activatedRouteSubscription = this.activatedRoute.queryParams.subscribe(data => {
      const defaultParams = merge({}, this.dataId, JSON.parse(get(data, 'defaultParams', '{}')));
      this.setupService.openTestSetup(
        this.setupId,
        this.setupConfig,
        defaultParams,
        null,
        null,
        this.agentBasedConfig
      );
    });
    this.exchange.notifyListeners('new_test', { noValue: true });
    this.templateService.closeDialog?.subscribe(status => {
      if (status && this.templateDialogRef) {
        this.templateDialogRef.close();
      }
    });
  }

  ngAfterViewInit() {
    this.createContentComponent(1);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.activatedRouteSubscription) {
      this.activatedRouteSubscription.unsubscribe();
    }
    if (this.unsubscribe) {
      this.unsubscribe.unsubscribe();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
  get(object: any, path: string): any {
    return get(object, path);
  }

  //-------------- Control && Validation -----------------------
  toggleTabChange(index: number): void {
    this.setupService.toggleTabChange(index);
    const tabIndex = index + 1;
    // create the component only once when its not available
    if (!this.testSetupTabRef['contentRef' + `${tabIndex}`].length) {
      this.createContentComponent(tabIndex);
    }
  }

  toggleClose(): void {
    this.setupService.toggleClose(() => this.onClose.emit(true));
  }

  toggleReOpen(): void {
    this.setupService.reOpen();
  }

  toggleFinish(): void {
    this.setupService.onNext();
    this.setupService.openTestStatusDialog(
      this.setupService.editMode ? 'Updating Voip' : 'Creating Voip',
      this.setupService.editMode ? 'Update' : 'Add'
    );
    const lmapObject = this.setupService.createLMAPObject();
    if (this.setupService.editMode) {
      const scheduleId = this.setupService.defaultParamsById['scheduleId'];
      const testStatus = this.testListService.getTestListById(scheduleId)?.getTestStatus();
      const applyValue = testStatus === TEST_STATE.RUNNING ? 'next-run-restart-now' : 'next-run';
      lmapObject['runlater'] = 'yes';
      this.subscription = this.setupService
        ._updateControllerTest({
          opt: { id: this.setupService.defaultParamsById['scheduleId'], apply: applyValue },
          config: lmapObject
        })
        .subscribe(
          response => {
            const message = 'VoIP test has been updated';
            this.notificationService.onSuccess(message);
            this.testListService.updateTestResponse();
            this.setupService.dialogRef.close();
            this.toggleClose();
            this.exchange.notifyListeners('new_test', response);
          },
          error => {
            const message = 'Failed to update VoIP test';
            this.notificationService.onFail(message);
            this.setupService.dialogRef.close();
            this.toggleClose();
          }
        );
    } else {
      this.subscription = this.setupService._saveControllerTest(lmapObject).subscribe(
        response => {
          const message = 'VoIP test has been created';
          this.notificationService.onSuccess(message);
          this.testListService.updateTestResponse();
          this.setupService.dialogRef.close();
          this.toggleClose();
          this.exchange.notifyListeners('new_test', response);
        },
        error => {
          const message = 'Failed to create VoIP test';
          this.notificationService.onFail(message);
          this.setupService.dialogRef.close();
          this.toggleClose();
        }
      );
    }
  }

  getSetupService(): VoipTestService {
    return this.setupService;
  }
  getSetupTab(): BaseSetupTab {
    return this.setupService.currentTab;
  }

  getFactoryResolver(): ComponentFactoryResolver {
    return this.factoryResolver;
  }

  getContentViewRef(index: number): ViewContainerRef {
    return this.testSetupTabRef['contentRef' + `${index}`];
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolveComponentType(pageId: string): any {
    switch (pageId) {
      case 'voip-test.html':
        return VoipTestComponent;

      default: {
        console.error('Unrecognized template name:', pageId);
        return null;
      }
    }
  }

  downloadTestConfig(): void {
    this.setupService.onNext();
    const lmapObject = this.setupService.createLMAPObject();
    this.setupService._downloadTestConfig(lmapObject);
  }

  saveTestConfig(): void {
    this.setupService.onNext();
    const proxyConfig = {};
    proxyConfig['proxyConfig'] = this.setupService.createProxyConfig();
    proxyConfig['runType'] = this.setupService.output[ConfigFieldMapping.RUN_TYPE];
    proxyConfig['thresholdWarning'] =
      this.setupService.output[ConfigFieldMapping.THRESHOLD_WARNING];
    proxyConfig['thresholdCritical'] =
      this.setupService.output[ConfigFieldMapping.THRESHOLD_CRITICALL];
    const configParams = { ...proxyConfig };
    const allTestParams = this.setupService._createTemplateFormat(
      configParams,
      this.setupService.output[ConfigFieldMapping.SELECTED_ORIGINATOR],
      this.setupService.output[ConfigFieldMapping.SELECTED_DESTINATION]
    );
    this.editDeviceDialogRef = this.dialog.open(SaveTemplateComponent, {
      width: '70rem',
      disableClose: true,
      data: { deviceInfo: allTestParams, editMode: true, testType: TestTypeValue.VOIP }
    });
  }

  openTemplates(): void {
    const param = {
      id: 'setupService.test_templates',
      callBackService: this,
      callBackFuntion: this.openTemplatesDialog
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }
  // eslint-disable-next-line
  openTemplatesDialog(service: any): void {
    service.toggleClose();
    service.templateDialogRef = service.dialog.open(TemplateTabsComponent, {
      width: '100rem',
      minHeight: '50rem',
      height: 'calc(100vh - 20px)',
      disableClose: true,
      data: {
        filterType: 'voip',
        myTemplatesColumnHeaders: ['name', 'accessLevel', 'lastModified', 'selectActions'],
        sharedTemplatesColumnHeaders: [
          'name',
          'accessLevel',
          'owner',
          'lastModified',
          'selectActions'
        ]
      }
    });
  }
}
